import { forkJoin, Observable, of, throwError, EMPTY } from 'rxjs';

import { map, catchError, tap, switchMap, take, mergeMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store, select } from '@ngrx/store';
import { GRAPH_URL, TENANT_ID } from '../graph-api.config';
import { UserGraphApiData, UserProfile } from '../models/graph-api-data';
import { Auth } from '../models/auth';
import { getADUser } from '../selectors/auth.selectors';
import { flatten } from 'lodash';

const UUID_REGEX = new RegExp('[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}');
let cachedUsers: { [key: string]: UserProfile } = {};

@Injectable()
export class GraphApi {
  constructor(private store: Store<Auth>, private http: HttpClient) {}

  /**
   * Does a query to the graph api for, it doesn't support $search, so we search by displayName and given name.
   * displayName starts with lastName.
   *
   * @param [searchString]
   * @returns
   */
  searchUsers(
    searchString?: string,
    options?: { includeLdap: boolean }
  ): Observable<UserProfile[]> {
    const params = {
      $filter: 'accountEnabled eq true'
    };
    if (searchString && searchString.length > 0) {
      // Search by First name
      searchString = searchString.replace("'", "''");
      const names = searchString.split(' ');
      params.$filter += ` and (startswith(givenName,'${names[0]}')`;

      // Search by e-mail
      if (names.length === 1) {
        params.$filter += ` or startswith(mail,'${names}')`;
        if (options && options.includeLdap) {
          params.$filter += ` or startswith(mailNickname,'${names}')`;
        }
      }

      // Search by last name
      if (names.length === 1 || names[1] === '') {
        params.$filter += ` or startswith(surname,'${names[0]}'))`;
      } else {
        params.$filter += ` and startswith(surname,'${names[1]}'))`;
      }
    }

    return this.http
      .get<UserGraphApiData>(GRAPH_URL + TENANT_ID + '/users', {
        params: params
      })
      .pipe(
        map(res => {
          return (<UserGraphApiData>res).value;
        }),
        catchError(error => {
          this.throwErrorMessage(error, 'Error searching users');
          return [];
        })
      );
  }

  /**
   * Retrieves all the user data from the passed in user id.
   * The function also caches users, to minimize the amount of graph requests.
   *
   * @param ids user object id
   * @returns UserProfile list
   */
  getUsers(ids: string[]): Observable<UserProfile[]> {
    ids = ids.filter(id => UUID_REGEX.test(id));
    if (ids.length === 0) {
      throwError('Invalid user ID');
      return EMPTY;
    }

    const nonCachedUserIds = ids.filter(id => !this.getCacheUsers(id));
    if (nonCachedUserIds.length > 0) {
      return this.retrieveUsers(nonCachedUserIds).pipe(
        tap(users => this.cacheUsers(users)),
        map(_users => {
          return ids.map(id => this.getCacheUsers(id));
        })
      );
    }
    return of(ids.map(id => this.getCacheUsers(id)));
  }

  /**
   * Retrieves all the user data from the passed in user ldap.
   * which is the mailNickname in the graph url.
   *
   * @deprecated This method was replaced by a method with same name on the MS Graph API (newer version).
   *
   * @param ldaps user ldap list
   * @returns UserProfile list
   */
  getUsersByLdap(ldaps: string[]): Observable<UserProfile[]> {
    if (ldaps.length === 0) {
      throwError('Invalid user ID');
      return EMPTY;
    }
    let i, j;
    const chunk = 10;
    const requestChunks: Observable<UserProfile[]>[] = [];
    for (i = 0, j = ldaps.length; i < j; i += chunk) {
      const temparray = ldaps.slice(i, i + chunk);
      requestChunks.push(this.getUsersByLdapInChunks(temparray));
    }
    return forkJoin(requestChunks).pipe(
      map(response => {
        return flatten(response);
      })
    );
  }

  /**
   * Retrieves all the user data from the passed in user ldaps.
   * This array should have a max of 10 items, Graph api doesn't allow for more conditions at one time.
   * which is the mailNickname in the graph url.
   *
   * @param ldaps user ldap list
   * @returns UserProfile list
   */
  private getUsersByLdapInChunks(ldaps: string[]): Observable<UserProfile[]> {
    const query = `(mailNickname eq '${ldaps.join("' or mailNickname eq '")}')`;
    return this.http
      .get<UserGraphApiData>(GRAPH_URL + TENANT_ID + '/users', {
        params: { $filter: query }
      })
      .pipe(
        map(res => {
          return (<UserGraphApiData>res).value;
        }),
        catchError(error => {
          this.throwErrorMessage(error, 'Error searching users');
          return [];
        }),
        take(1)
      );
  }

  /**
   * Gets the full user object including the manager info
   *
   * @param id can be email or objectId
   * @returns
   */
  getFullUserDetails(id: string): Observable<UserProfile> {
    return forkJoin([
      this.getUserByEmail(id),
      this.getUserManager(id),
      this.getUserThumbnail(id)
    ]).pipe(
      map(([userDetails, userManager, thumbnailUrl]) => {
        return {
          ...userDetails,
          manager: userManager,
          thumbnailPhotoUrl: thumbnailUrl
        };
      }),
      catchError(error => {
        this.throwErrorMessage(error, 'Error retrieving user profile');
        return [];
      })
    );
  }

  getUserThumbnail(id: string): Observable<string> {
    const options = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(`${GRAPH_URL + TENANT_ID}/users/${id}/thumbnailPhoto`, options).pipe(
      switchMap(resp => {
        return new Observable(observer => {
          const reader = new FileReader();
          reader.onload = () => {
            observer.next(<string>reader.result);
            observer.complete();
          };
          reader.readAsDataURL(resp);
        }) as Observable<string>;
      }),
      catchError(() => {
        return of('');
      })
    );
  }

  getUserByEmail(email: string): Observable<UserProfile> {
    return this.http.get<UserGraphApiData>(GRAPH_URL + TENANT_ID + '/users/' + email).pipe(
      catchError(error => {
        this.throwErrorMessage(error, 'Error retrieving user profile');
        return [];
      })
    );
  }

  /**
   * Gets the manager of the user
   *
   * @param id can be the objectId or user email
   * @returns
   */
  getUserManager(id: string): Observable<UserProfile> {
    return this.http
      .get<UserGraphApiData>(GRAPH_URL + TENANT_ID + '/users/' + id + '/manager')
      .pipe(
        catchError(() => {
          return of(null);
        })
      );
  }

  /**
   * Uses the getUsers function to retrieve a single user profile
   *
   * @param id user object id
   * @returns UserProfile
   */
  getUser(id: string): Observable<UserProfile> {
    return this.getUsers([id]).pipe(
      take(1),
      map(res => {
        return res[0];
      })
    );
  }

  /**
   * Retrieves the user profile for the current user
   *
   * @returns UserProfile
   */
  getCurrentUser(): Observable<UserProfile> {
    return this.store.pipe(
      select(getADUser),
      mergeMap(user => {
        return this.getUser(user.profile.oid);
      })
    );
  }

  clearUserCache(): void {
    cachedUsers = {};
  }

  private getCacheUsers(userId: string) {
    return cachedUsers[userId];
  }

  /**
   * Caches users in the cachedUsers object by the user objectId
   *
   * @param users UserProfile list
   */
  private cacheUsers(users: UserProfile[]) {
    users.forEach(user => (cachedUsers[user.objectId] = user));
  }

  /**
   * Uses the 'getObjectsByObjectIds' endpoint on the azure graph to retrieve users by the user object ids.
   *
   * @param ids user object id
   * @returns UserProfile list
   */
  private retrieveUsers(ids: string[]): Observable<UserProfile[]> {
    return this.http
      .post<UserGraphApiData>(GRAPH_URL + TENANT_ID + '/getObjectsByObjectIds', {
        objectIds: ids,
        types: ['user']
      })
      .pipe(
        map(res => {
          return res.value;
        }),
        catchError(error => {
          this.throwErrorMessage(error, 'Error retrieving user profiles');
          return [];
        })
      );
  }

  // tslint:disable-next-line:no-any
  private throwErrorMessage(error: any, message?: string) {
    const json = error?.json ? error.json() : JSON.stringify(error || 'Unknown');

    const errorMessage = message ? message + ' | ' + json : json;
    throwError(errorMessage);
  }
}
